/**
 * The three gate-pass work queues, fetched once per store scope.
 *
 * The hub's stat cards and two of its four tabs read the same three lists, so they
 * are loaded here rather than per card or per tab — four cards each fetching their
 * own count is the shape this module has been bitten by before.
 *
 * Deliberately NOT keyed on location.pathname: tab state is local, and a
 * pathname-keyed effect refires on every navigation.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { getPendingApprovals, getOutstandingGatePasses } from '../api/gatePass';
import { toInventoryList } from '../api/inventory-request';
import { GATE_PASS_STAGES } from '../constants/gatePass';
import { isOverdue } from '../utils/gatePassEntity';

export default function useGatePassQueues(storeId) {
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [gateQueue, setGateQueue] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const scope = storeId ? { store_id: storeId } : {};
      const [approvals, gate, out] = await Promise.all([
        // API: GET /inventory/gate-pass/pending-approval/?stage=STORE
        getPendingApprovals({ ...scope, stage: GATE_PASS_STAGES.STORE }),
        // API: GET /inventory/gate-pass/pending-approval/?stage=SECURITY
        getPendingApprovals({ ...scope, stage: GATE_PASS_STAGES.SECURITY }),
        // API: GET /inventory/gate-pass/outstanding/
        getOutstandingGatePasses(scope),
      ]);
      setApprovalQueue(toInventoryList(approvals));
      setGateQueue(toInventoryList(gate));
      setOutstanding(toInventoryList(out));
    } catch (e) {
      setError(e.message);
      setApprovalQueue([]);
      setGateQueue([]);
      setOutstanding([]);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  // Overdue is derived, not a fourth request — /outstanding already carries the
  // expected return date, and the cut-off is "expected date has passed".
  const overdue = useMemo(() => outstanding.filter(gp => isOverdue(gp)), [outstanding]);

  return { approvalQueue, gateQueue, outstanding, overdue, loading, error, reload: load };
}
